// src/frontend/src/pages/Security.tsx
import React from 'react';
import HomeLayout from '../components/Layout/HomeLayout';

const Security: React.FC = () => {
  return (
    <HomeLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-6">
            Security & Trust
          </h1>
          <p className="max-w-3xl mx-auto text-xl text-gray-600">
            Your data security and privacy are our top priorities. Learn how we protect your information 
            and maintain the trust of thousands of project managers worldwide.
          </p>
        </div>

        {/* Security Overview */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Enterprise-Grade Security</h3>
              <p className="text-gray-600">Bank-level encryption and security measures protect your data at all times.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Privacy by Design</h3>
              <p className="text-gray-600">Your conversations and data are private, never shared, and used only for your coaching.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Compliance Ready</h3>
              <p className="text-gray-600">Built to meet enterprise compliance requirements including GDPR and SOC 2.</p>
            </div>
          </div>
        </div>

        {/* Data Protection */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Data Protection & Encryption</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Encryption Standards</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>AES-256 encryption</strong> for data at rest</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>TLS 1.3</strong> for data in transit</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>End-to-end encryption</strong> for sensitive communications</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>Regular key rotation</strong> and secure key management</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Infrastructure Security</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>AWS/Google Cloud</strong> secure infrastructure</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>Multi-zone redundancy</strong> for high availability</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>DDoS protection</strong> and threat monitoring</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>Regular security audits</strong> and penetration testing</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Privacy & Data Handling */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Privacy & Data Handling</h2>
          
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">What We Collect</h3>
                <div className="space-y-3 text-gray-700">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Account information (name, email, organization)</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Coaching session conversations and responses</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Usage analytics to improve the platform</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Payment information (securely processed by Stripe)</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">How We Use It</h3>
                <div className="space-y-3 text-gray-700">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Provide personalized AI coaching feedback</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Track your progress and improvement</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Improve our AI models and platform features</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Send important account and service notifications</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">🔒 Your Data Rights</h4>
              <p className="text-blue-800 text-sm">
                You have full control over your data. You can export your coaching sessions, update your information, 
                or delete your account at any time through your settings. We never sell your data or use it for advertising.
              </p>
            </div>
          </div>
        </div>

        {/* Compliance & Certifications */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Compliance & Certifications</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">SOC 2 Type II</h3>
              <p className="text-sm text-gray-600">Security and availability controls audited by independent third parties</p>
              <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">In Progress</span>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">GDPR Compliant</h3>
              <p className="text-sm text-gray-600">Full compliance with European data protection regulations</p>
              <span className="inline-block mt-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Certified</span>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">ISO 27001</h3>
              <p className="text-sm text-gray-600">Information security management system certification</p>
              <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Planned</span>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">CCPA Compliant</h3>
              <p className="text-sm text-gray-600">California Consumer Privacy Act compliance for US users</p>
              <span className="inline-block mt-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Certified</span>
            </div>
          </div>
        </div>

        {/* Security Practices */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Security Practices</h2>
          
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Access Controls</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Multi-factor authentication (MFA) for all team accounts</li>
                  <li>• Role-based access control (RBAC) for team features</li>
                  <li>• Regular access reviews and deprovisioning</li>
                  <li>• Principle of least privilege for all systems</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-4 mt-6">Monitoring & Response</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• 24/7 security monitoring and alerting</li>
                  <li>• Automated threat detection and response</li>
                  <li>• Regular vulnerability assessments</li>
                  <li>• Incident response plan with defined procedures</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Development Security</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Secure coding practices and code reviews</li>
                  <li>• Automated security testing in CI/CD pipeline</li>
                  <li>• Regular dependency updates and vulnerability scanning</li>
                  <li>• Secure software development lifecycle (SSDLC)</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-4 mt-6">Employee Training</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Regular security awareness training</li>
                  <li>• Background checks for all team members</li>
                  <li>• Confidentiality agreements and security policies</li>
                  <li>• Incident response training and drills</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* AI & Data Processing */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">AI & Data Processing Security</h2>
          
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-8 border border-blue-200">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">AI Model Security</h3>
                <p className="text-gray-700 mb-4">
                  We use Anthropic's Claude AI for generating coaching feedback and stakeholder responses. 
                  Your conversations are processed securely and never stored by the AI provider.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• No persistent storage of conversations by AI provider</li>
                  <li>• Encrypted transmission of all AI requests</li>
                  <li>• Regular security audits of AI integrations</li>
                  <li>• Anonymization of data before AI processing</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Data Retention & Deletion</h3>
                <p className="text-gray-700 mb-4">
                  We retain your data only as long as necessary to provide our services and comply with legal requirements.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Coaching sessions: Retained until account deletion</li>
                  <li>• Account data: Deleted within 30 days of account closure</li>
                  <li>• Backup data: Purged within 90 days</li>
                  <li>• Analytics: Aggregated data only, no personal identifiers</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Incident Response */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Incident Response & Business Continuity</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Security Incident Response</h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start">
                  <span className="inline-block w-6 h-6 bg-red-100 text-red-600 rounded-full text-sm font-bold text-center mr-3 mt-0.5">1</span>
                  <span><strong>Detection:</strong> Automated monitoring alerts security team within minutes</span>
                </div>
                <div className="flex items-start">
                  <span className="inline-block w-6 h-6 bg-orange-100 text-orange-600 rounded-full text-sm font-bold text-center mr-3 mt-0.5">2</span>
                  <span><strong>Assessment:</strong> Rapid evaluation of impact and affected systems</span>
                </div>
                <div className="flex items-start">
                  <span className="inline-block w-6 h-6 bg-yellow-100 text-yellow-600 rounded-full text-sm font-bold text-center mr-3 mt-0.5">3</span>
                  <span><strong>Containment:</strong> Immediate isolation and mitigation of threats</span>
                </div>
                <div className="flex items-start">
                  <span className="inline-block w-6 h-6 bg-green-100 text-green-600 rounded-full text-sm font-bold text-center mr-3 mt-0.5">4</span>
                  <span><strong>Communication:</strong> Customer notification within 24-72 hours if affected</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Business Continuity</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>99.9% uptime SLA with redundant infrastructure</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Automated daily backups with cross-region replication</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Disaster recovery plan with 4-hour RTO</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Regular DR testing and plan updates</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Trust & Transparency */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Trust & Transparency</h2>
          
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Security Transparency Report</h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                We believe in complete transparency about our security practices. Here's our commitment to you:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 mb-2">99.9%</div>
                <div className="text-sm text-gray-600">Uptime over the last 12 months</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600 mb-2">0</div>
                <div className="text-sm text-gray-600">Data breaches since inception</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600 mb-2">&lt;2h</div>
                <div className="text-sm text-gray-600">Average incident response time</div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-3">🛡️ Security Contact</h4>
              <p className="text-blue-800 mb-3">
                Found a security vulnerability? We take security seriously and appreciate responsible disclosure.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a 
                  href="mailto:security@pmcoach.app" 
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  Report Security Issue
                </a>
                <span className="text-blue-700 text-sm self-center">
                  Expected response time: &lt;24 hours
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact & Resources */}
        <div className="bg-gradient-to-r from-gray-900 to-blue-900 rounded-xl p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-4">Questions About Our Security?</h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Our security team is here to help. Whether you're evaluating PM Coach for your organization 
            or need technical details for compliance, we're happy to provide additional information.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:security@pmcoach.app"
              className="inline-flex items-center px-6 py-3 bg-white text-gray-900 rounded-md hover:bg-gray-100 font-medium"
            >
              Contact Security Team
            </a>
            <a
              href="mailto:enterprise@pmcoach.app"
              className="inline-flex items-center px-6 py-3 border-2 border-white text-white rounded-md hover:bg-white hover:text-gray-900 font-medium"
            >
              Enterprise Inquiries
            </a>
          </div>
          <p className="text-gray-400 text-sm mt-4">
            Security documentation and additional compliance materials available upon request
          </p>
        </div>
      </div>
    </HomeLayout>
  );
};

export default Security;